import { useOutsideClick } from '@sequencehq/utils'
import { useMemo, useRef, useState } from 'react'
import {
  Text,
  IconArrowRight,
  IconSettingsSliderHorizontal,
  IconCalendarDays,
  IconBuildings,
  Icon3dBox,
  IconTag,
  GreyGrey30,
  GreyGrey60,
  ActiveFilter,
  MagicTableFilterConfig,
  MagicTableFilters,
  Primary13Regular,
  Button,
  InputSelectControl,
  Popover,
  Box,
  Flex
} from '@sequencehq/core-components'
import { Currency } from '@sequencehq/api'
import { useSelectedCurrency } from 'RevenueRecognition/view/utils/useSelectedCurrency'
import {
  isBeforeMonth,
  monthEndOf,
  monthStartOf,
  MONTH_END_OPTIONS,
  MONTH_START_OPTIONS,
  previousMonthStartFrom
} from '../../utils/monthOptions'
import { RevenueWaterfallQueryParams, RevenueWaterfallGrouping } from './types'
import { useAvailableCurrencies } from 'RevenueRecognition/view/utils/useAvailableCurrencies'
import { customerIdFilterConfig } from 'modules/Customer'
import { productIdFilterConfig } from 'modules/Products'
import { useXeroIntegration } from 'lib/hooks/useXeroIntegration'
import { splitCsv, joinCsv } from '@sequencehq/utils'

interface Props {
  filters: RevenueWaterfallQueryParams
  onFilterChange: (filters: RevenueWaterfallQueryParams) => void
}

type WaterfallFilterParams = {
  customerId?: string
  productId?: string
}

const entityFilterConfigs: MagicTableFilterConfig<WaterfallFilterParams>[] = [
  { ...customerIdFilterConfig, paramName: 'customerId' },
  { ...productIdFilterConfig, paramName: 'productId' }
]

const buildEntityActiveFilters = (
  filters: RevenueWaterfallQueryParams
): ActiveFilter[] => {
  const result: ActiveFilter[] = []
  const customerValues = splitCsv(filters.customerId)
  const productValues = splitCsv(filters.productId)

  if (customerValues.length) {
    result.push({
      type: 'multiSelect',
      paramName: 'customerId',
      values: customerValues
    })
  }
  if (productValues.length) {
    result.push({
      type: 'multiSelect',
      paramName: 'productId',
      values: productValues
    })
  }
  return result
}

export const RevenueWaterfallTableFilters = ({
  filters,
  onFilterChange
}: Props) => {
  const { currency, setCurrency } = useSelectedCurrency()
  const { availableCurrencies: enabledCurrencies } = useAvailableCurrencies()

  const currencyOptions = useMemo(() => {
    const uniqueCurrencies = new Set(enabledCurrencies ?? [])
    if (currency) {
      uniqueCurrencies.add(currency)
    }

    return Array.from(uniqueCurrencies).map(availableCurrency => ({
      label: availableCurrency,
      value: availableCurrency
    }))
  }, [enabledCurrencies, currency])

  const handleBookedFromChange = (value: string) => {
    const nextBookedFrom = monthStartOf(value)

    let nextBookedTo = filters.bookedTo
    let nextRecognizedUpTo = filters.recognizedUpTo

    if (isBeforeMonth(nextBookedTo, nextBookedFrom)) {
      nextBookedTo = monthEndOf(nextBookedFrom)
    }

    if (isBeforeMonth(nextRecognizedUpTo, nextBookedTo)) {
      nextRecognizedUpTo = nextBookedTo
    }

    onFilterChange({
      ...filters,
      bookedFrom: nextBookedFrom,
      bookedTo: nextBookedTo,
      recognizedUpTo: nextRecognizedUpTo
    })
  }

  const handleBookedToChange = (value: string) => {
    const nextBookedTo = monthEndOf(value)

    let nextBookedFrom = filters.bookedFrom
    let nextRecognizedUpTo = filters.recognizedUpTo

    if (isBeforeMonth(nextBookedTo, nextBookedFrom)) {
      nextBookedFrom = previousMonthStartFrom(nextBookedTo)
    }

    if (isBeforeMonth(nextRecognizedUpTo, nextBookedTo)) {
      nextRecognizedUpTo = nextBookedTo
    }

    onFilterChange({
      ...filters,
      bookedFrom: nextBookedFrom,
      bookedTo: nextBookedTo,
      recognizedUpTo: nextRecognizedUpTo
    })
  }

  const handleRecognizedUpToChange = (value: string) => {
    const nextRecognizedUpToCandidate = monthEndOf(value)

    const nextBookedTo = filters.bookedTo
    let nextBookedFrom = filters.bookedFrom
    let nextRecognizedUpTo = nextRecognizedUpToCandidate

    if (isBeforeMonth(nextRecognizedUpTo, nextBookedTo)) {
      nextRecognizedUpTo = nextBookedTo
    }

    // Defensive: if filter state somehow has bookedFrom after bookedTo,
    // pull bookedFrom back.
    if (isBeforeMonth(nextBookedTo, nextBookedFrom)) {
      nextBookedFrom = previousMonthStartFrom(nextBookedTo)
    }

    onFilterChange({
      ...filters,
      bookedFrom: nextBookedFrom,
      bookedTo: nextBookedTo,
      recognizedUpTo: nextRecognizedUpTo
    })
  }

  const entityActiveFilters = useMemo(
    () => buildEntityActiveFilters(filters),
    [filters]
  )

  const handleEntityActiveFiltersChange = (next: ActiveFilter[]) => {
    const customer = next.find(
      filter =>
        filter.type === 'multiSelect' && filter.paramName === 'customerId'
    )
    const product = next.find(
      filter =>
        filter.type === 'multiSelect' && filter.paramName === 'productId'
    )

    onFilterChange({
      ...filters,
      customerId: joinCsv(
        customer && customer.type === 'multiSelect' ? customer.values : []
      ),
      productId: joinCsv(
        product && product.type === 'multiSelect' ? product.values : []
      )
    })
  }

  const handleGroupingChange = (value: string) => {
    onFilterChange({
      ...filters,
      groupBy: value as RevenueWaterfallGrouping
    })
  }

  const handleSubGroupingChange = (value: string) => {
    onFilterChange({
      ...filters,
      subGroupBy: value as RevenueWaterfallGrouping
    })
  }

  return (
    <Flex
      flexDirection="column"
      borderBottom="1px solid"
      borderColor={GreyGrey30}
    >
      <Flex
        minHeight="48px"
        paddingLeft="16px"
        paddingRight="16px"
        paddingTop="8px"
        paddingBottom="8px"
        justifyContent="space-between"
        alignItems="start"
      >
        <Flex gap="8px" alignItems="center" flexWrap="wrap">
          <Text {...Primary13Regular}>Booked from</Text>
          <InputSelectControl
            data-testid="revrec.waterfall.filters.bookedFrom"
            triggerStyle={{ width: '110px' }}
            options={MONTH_START_OPTIONS}
            initialValue={filters.bookedFrom}
            onChange={handleBookedFromChange}
          />
          <IconArrowRight color={GreyGrey60} />
          <InputSelectControl
            data-testid="revrec.waterfall.filters.bookedTo"
            triggerStyle={{ width: '110px' }}
            options={MONTH_END_OPTIONS}
            initialValue={filters.bookedTo}
            onChange={handleBookedToChange}
          />
          <Text {...Primary13Regular}>and recognized up to</Text>
          <InputSelectControl
            data-testid="revrec.waterfall.filters.recognizedUpTo"
            triggerStyle={{ width: '110px' }}
            options={MONTH_END_OPTIONS}
            initialValue={filters.recognizedUpTo}
            onChange={handleRecognizedUpToChange}
          />
          <Text {...Primary13Regular}>in</Text>
          <Box width="84px">
            {!!currency && currencyOptions.length > 0 && (
              <InputSelectControl
                data-testid="revrec.home.currencySelect"
                options={currencyOptions}
                value={currency}
                onChange={newCurrency => setCurrency(newCurrency as Currency)}
              />
            )}
          </Box>

          <MagicTableFilters
            filters={entityFilterConfigs}
            activeFilters={entityActiveFilters}
            onChangeActiveFilters={handleEntityActiveFiltersChange}
          />
        </Flex>

        <RevenueWaterfallViewSettingsControls
          grouping={filters.groupBy}
          onGroupingChange={handleGroupingChange}
          subGrouping={filters.subGroupBy}
          onSubGroupingChange={handleSubGroupingChange}
        />
      </Flex>
    </Flex>
  )
}

const baseGroupingOptions = [
  {
    label: 'Customer',
    value: 'CUSTOMER',
    icon: <IconBuildings />
  },
  {
    label: 'Product',
    value: 'PRODUCT',
    icon: <Icon3dBox />
  },
  {
    label: 'Month',
    value: 'MONTH',
    icon: <IconCalendarDays />
  }
]

// Grouping by Xero ledger code is only meaningful when the Xero integration
// is enabled for the account, so it is appended conditionally.
const xeroCodeGroupingOption = {
  label: 'Xero code',
  value: 'XERO_CODE',
  icon: <IconTag />
}

const RevenueWaterfallViewSettingsControls = ({
  grouping,
  onGroupingChange,
  subGrouping,
  onSubGroupingChange
}: {
  grouping: string
  onGroupingChange: (value: string) => void
  subGrouping: string
  onSubGroupingChange: (value: string) => void
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const popoverContentRef = useRef<HTMLElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)

  const { xeroIntegration } = useXeroIntegration()

  const groupingOptions = useMemo(() => {
    const xeroEnabled = Boolean(xeroIntegration?.enabled)

    return xeroEnabled
      ? [...baseGroupingOptions, xeroCodeGroupingOption]
      : baseGroupingOptions
  }, [xeroIntegration?.enabled])

  useOutsideClick({
    ref: popoverContentRef,
    handler: event => {
      if (triggerRef.current?.contains(event.target as Node)) {
        return
      }

      const allPopovers = document.querySelectorAll('[role="dialog"]')
      const childPopovers = Array.from(allPopovers).filter(
        el => el !== popoverContentRef.current
      )
      const clickedInsideChildPopover = childPopovers.some(popover =>
        popover.contains(event.target as Node)
      )

      if (!clickedInsideChildPopover) {
        setIsOpen(false)
      }
    }
  })

  return (
    <Popover
      trigger="click"
      showArrow={false}
      variant="table"
      offset={[-24, 0]}
      width="320px"
      maxWidth="320px"
      closeOnBlur={false}
      isOpen={isOpen}
      contentRef={popoverContentRef}
      content={
        <Flex px="12px" py="10px" flexDirection="column" gap="4px" width="100%">
          <Flex
            height="40px"
            alignItems="center"
            justifyContent="space-between"
          >
            <Text {...Primary13Regular}>Grouping</Text>
            <InputSelectControl
              data-testid="revrec.waterfall.viewSettings.grouping"
              options={groupingOptions}
              initialValue={grouping}
              onChange={onGroupingChange}
              triggerStyle={{
                width: '185px'
              }}
            />
          </Flex>
          <Flex
            height="40px"
            alignItems="center"
            justifyContent="space-between"
          >
            <Text {...Primary13Regular}>Sub-grouping</Text>
            <InputSelectControl
              data-testid="revrec.waterfall.viewSettings.subGroupBy"
              options={groupingOptions.filter(
                option => option.value !== grouping
              )}
              initialValue={subGrouping}
              onChange={onSubGroupingChange}
              triggerStyle={{
                width: '185px'
              }}
            />
          </Flex>
        </Flex>
      }
    >
      <Button
        data-testid="revrec.waterfall.viewSettings"
        ref={triggerRef}
        onClick={() => setIsOpen(!isOpen)}
        variant="secondary"
        leadingIcon={<IconSettingsSliderHorizontal />}
      >
        View settings
      </Button>
    </Popover>
  )
}
